import React, { useEffect } from "react";
import styled from "styled-components";

import { configurationManager } from "../../../../shared/core/service/services";
import type { Keyboard } from "../../../../shared/domains/pincode/keyboard";
import type { PincodeSubmission } from "../../../../shared/domains/pincode/pincode";
import { usePincode } from "../../../../shared/domains/pincode/use-pincode";
import { isDefined } from "../../../../shared/utils/assert";
import { useObservable } from "../../../../shared/utils/observable";
import { repeat } from "../../../../shared/utils/utils";
import { theme } from "../../styles/theme";
import { DefaultButton } from "../buttons/default-button";
import { ErrorMessage } from "../error-message";
import { MainColorSpinner } from "../spinner";
import { ResetIcon } from "../svg/reset-icon";

interface PincodeKeyboardProps {
  keyboard: Keyboard | undefined;
  onSubmit: (submission: PincodeSubmission) => void;
  title?: string;
  className?: string;
  errorMessage?: string;
  loadingKeyboard?: boolean;
  keyColor?: string;
}

const GRID_COLUMNS = 4;
export const GRID_ROWS = 4;
export const KEYBOARD_SPACING = 12;
const TOUCH_SIZE = 56;

/** The keypad as the redesign draws it on both platforms: outlined keys under a row of dots. */
export const PincodeKeyboard = (props: PincodeKeyboardProps) => {
  const { keyboard, onSubmit, className, title, errorMessage, loadingKeyboard, keyColor } = props;
  const configuration = useObservable(configurationManager.configuration);
  const pincodeMaxLength = configuration.pincode.maxLength;
  const { pincode, enterDigit, resetCode } = usePincode<number>(pincodeMaxLength);

  useEffect(() => {
    if (pincode.length >= pincodeMaxLength) {
      resetCode();
      if (onSubmit && keyboard) {
        onSubmit({ id: keyboard.id, value: pincode });
      }
    }
  }, [keyboard, onSubmit, pincode, resetCode, pincodeMaxLength]);

  return (
    <KeyboardContainer className={className}>
      <Header>
        {!!title && <Title>{title}</Title>}
        {!!errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      </Header>
      <CodeRow>
        {/* Equal-width flanks keep the dots optically centred while the clear key sits flush right. */}
        <SideZone />
        <Code>
          {repeat(pincodeMaxLength, (index) => (
            <Circle key={`indicator-${index}`} $filled={index < pincode.length} />
          ))}
        </Code>
        <SideZone>
          <ClearButton onClick={resetCode} aria-label="Clear PIN">
            <ResetIcon />
          </ClearButton>
        </SideZone>
      </CodeRow>
      {loadingKeyboard || !isDefined(keyboard) ? (
        <SpinnerContainer>
          <MainColorSpinner />
        </SpinnerContainer>
      ) : (
        <Grid>
          {repeat(GRID_ROWS, (rowIndex) => (
            <KeyboardRow key={rowIndex}>
              {repeat(GRID_COLUMNS, (cellIndex) => (
                // The index sent back to the server is the cell position, never the digit itself.
                <KeyboardTouch
                  key={`${rowIndex}-${cellIndex}`}
                  onClick={() => enterDigit(rowIndex * GRID_ROWS + cellIndex)}
                  $backgroundColor={keyColor}
                  // The glyph is drawn server side and its digit is deliberately unknown here, so
                  // the key is named by its place: enough to be reached, nothing given away.
                  aria-label={`Digit ${rowIndex * GRID_ROWS + cellIndex}`}
                >
                  <KeyboardImage src={`data:image/png;base64,${keyboard.images[rowIndex * 4 + cellIndex]}`} alt="" />
                </KeyboardTouch>
              ))}
            </KeyboardRow>
          ))}
        </Grid>
      )}
    </KeyboardContainer>
  );
};

const KEYBOARD_WIDTH = GRID_COLUMNS * TOUCH_SIZE + (GRID_COLUMNS - 1) * KEYBOARD_SPACING;

const KeyboardContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  text-align: center;
`;

const Title = styled.span`
  ${theme.text};
  font-size: 20px;
  line-height: 28px;
  color: ${theme.colors.blue[800]};
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-top: 16px;
  font-size: 14px;
  text-align: center;
`;

const CodeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${KEYBOARD_WIDTH}px;
  margin: 32px 0;
`;

const SideZone = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const Code = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Circle = styled.div<{ $filled: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 7px;
  margin: 0 9px;
  background-color: ${(props) => (props.$filled ? theme.colors.blue[700] : "#d9d9d9")};
`;

const ClearButton = styled(DefaultButton)`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.blue[700]};
  border-radius: 8px;

  svg {
    width: 20px;
    height: 18px;
    object-fit: contain;
    fill: ${theme.colors.blue[700]};
  }
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${KEYBOARD_SPACING}px;
`;

const KeyboardRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${KEYBOARD_SPACING}px;
`;

const KeyboardTouch = styled(DefaultButton)<{ $backgroundColor?: string }>`
  overflow: hidden;
  padding: 0;
  width: ${TOUCH_SIZE}px;
  height: ${TOUCH_SIZE}px;
  border: 1px solid #ededed;
  border-radius: 12px;
  background-color: ${(props) => props.$backgroundColor ?? "#ffffff"};

  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;

const KeyboardImage = styled.img`
  width: ${TOUCH_SIZE}px;
  height: ${TOUCH_SIZE}px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${KEYBOARD_WIDTH}px;
  height: ${GRID_ROWS * TOUCH_SIZE + (GRID_ROWS - 1) * KEYBOARD_SPACING}px;
`;
